// Grundstile zuerst: App importiert statisch Seiten-CSS (z. B. users/admin.css über die Zwei-Faktor-Seite). Stünde
// styles.css danach, überschrieben ihre Grundregeln (.field, .card …) die gleich spezifischen Regeln der Seiten-CSS.
import './styles.css';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { AuthProvider } from './lib/auth';
import { UiProvider } from './components/ui';
import { ApiError } from './lib/api';
import App from './App';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 15_000,
      refetchOnWindowFocus: true,
      retry: (count, err) => !(err instanceof ApiError && err.status >= 400 && err.status < 500) && count < 2,
    },
  },
});

// Theme aus lokaler Einstellung (hell/dunkel/automatisch)
try {
  const t = localStorage.getItem('theme');
  if (t === 'light' || t === 'dark') document.documentElement.setAttribute('data-theme', t);
} catch { /* ignore */ }

// Nach einem Update (./deploy/update.sh) gibt es die Programmdateien der alten Version nicht mehr. Ein Tab, der seit
// vorher offen ist, kann dann eine noch nicht geladene Seite nicht nachladen → einmal neu laden (holt die neue Version;
// Warenkorb, geparkte und noch nicht übertragene Bons liegen im Gerät und bleiben erhalten). Nur einmal je Minute,
// damit ein echter Fehler (Server weg) nicht zu einer Neulade-Schleife führt.
const RELOAD_KEY = 'kasse.reloadedForUpdate';
window.addEventListener('vite:preloadError', (event) => {
  try {
    const last = Number(sessionStorage.getItem(RELOAD_KEY)) || 0;
    if (Date.now() - last < 60_000) return; // gerade erst neu geladen → normale Fehlermeldung
    sessionStorage.setItem(RELOAD_KEY, String(Date.now()));
  } catch {
    return;
  }
  event.preventDefault();
  window.location.reload();
});

if ('serviceWorker' in navigator && import.meta.env.PROD) {
  window.addEventListener('load', () => {
    navigator.serviceWorker.register('/sw.js').catch(() => undefined);
  });
}

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <UiProvider>
        <AuthProvider>
          <BrowserRouter>
            <App />
          </BrowserRouter>
        </AuthProvider>
      </UiProvider>
    </QueryClientProvider>
  </StrictMode>,
);
